import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { ErrorMessage, useFormikContext } from 'formik';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@src/design-system/dropdown-menu';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { Label } from '@src/design-system/label';
import { InfiniteList } from '@src/design-system/molecules/infinite-list';
import reminderTemplateApi from '@src/features/reminders/services/reminder-template-api';
import {
  filterApprovedTemplates,
  getApprovedTemplatesQuery,
} from '@src/shared/features/reminders/helpers/template-approval';
import type { TReminderFormValues } from './schema';
import { inputVariants } from '@src/design-system/input';
import { cn } from '@src/design-system/helpers';
import { Dialog, DialogContent } from '@src/design-system/dialog';
import { CreateOrEditReminderTemplate } from '@src/shared/features/reminders/dialogs/create-or-edit-reminder-template';

export const TemplatesDropdown = () => {
  const { setFieldValue, values } = useFormikContext<TReminderFormValues>();
  const selectedTemplate = values.template;
  const [isCreateTemplateOpen, setIsCreateTemplateOpen] = useState(false);

  return (
    <div>
      <Label>Templates</Label>

      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={!values.notification_channel}
          className={cn(
            inputVariants(),
            'w-full flex justify-between items-center cursor-pointer text-sm',
            !selectedTemplate && 'text-gray-400'
          )}
        >
          {selectedTemplate?.name || 'Choose a template'}
          <ChevronDown size={16} />
        </DropdownMenuTrigger>
        <DropdownMenuContent className='p-0 gap-0'>
          <InfiniteList
            enableSearch
            query={{
              channel: values.notification_channel,
              ...getApprovedTemplatesQuery(values.notification_channel),
            }}
            fetchInfiniteData={reminderTemplateApi.useFetchInfinite}
            transformItems={filterApprovedTemplates}
            props={{
              listContainerProps: {
                className: 'px-0',
              },
              listProps: {
                className: 'gap-0',
              },
            }}
            renderItem={(template) => (
              <DropdownMenuItem
                className='border-b border-primary-200 rounded-none py-2.5'
                onClick={() => {
                  setFieldValue('template', template);
                  setFieldValue('content', template.content);
                  setFieldValue('attachments', template.attachments);
                }}
              >
                <div>{template.name}</div>
              </DropdownMenuItem>
            )}
          />

          <DropdownMenuItem
            className='rounded-md px-2 py-2 text-primary-1000'
            onSelect={(e) => {
              e.preventDefault();
              setIsCreateTemplateOpen(true);
            }}
          >
            Create New Template
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={isCreateTemplateOpen}
        onOpenChange={setIsCreateTemplateOpen}
      >
        <DialogContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          className='flex w-full max-w-[78%] flex-col overflow-hidden'
          dialogOverlayProps={{
            className: 'bg-transparent',
          }}
        >
          <CreateOrEditReminderTemplate />
        </DialogContent>
      </Dialog>

      <ErrorMessage
        name='template'
        component={CustomErrorMessage}
      />
    </div>
  );
};
